import { router } from '@inertiajs/react'

import AdminLayout from '../../../components/AdminLayout'
import { AdminCard, AdminHead } from '../../../components/admin/AdminHead'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface StockRequest {
  id: number
  customerName: string | null
  customerEmail: string
  location: string
  address: string | null
  build: string
  notified: boolean
  createdAt: string
}

interface Props {
  requests: StockRequest[]
  pendingCount: number
}

/**
 * Back-in-stock waitlist — leads captured by the customiser when a
 * shopper reached payment while the stand was out of stock. Rows still
 * owed an email take the accent wash; the status badge toggles them.
 */
export default function AdminStockRequestsIndex({ requests, pendingCount }: Props) {
  const toggle = (request: StockRequest) => {
    router.patch(
      `/admin/stock_requests/${request.id}`,
      { notified: !request.notified },
      { preserveScroll: true }
    )
  }

  return (
    <AdminLayout>
      <AdminHead
        eyebrow="Waitlist"
        title="Back in stock"
        sub={`${requests.length} total · ${pendingCount} waiting to be contacted`}
      />

      <AdminCard>
        {requests.length === 0 ? (
          <div className="p-[18px]">
            <EmptyState title="No waitlist requests yet." />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-surface-sunk">
                <TableHead>When</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Ships to</TableHead>
                <TableHead>Build</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} flagged={!request.notified}>
                  <TableCell className="text-ink-muted">{request.createdAt}</TableCell>
                  <TableCell>
                    <div>{request.customerName || '—'}</div>
                    <a
                      href={`mailto:${request.customerEmail}`}
                      className="text-[11px] text-ink-muted underline underline-offset-4 hover:text-signal-deep"
                    >
                      {request.customerEmail}
                    </a>
                  </TableCell>
                  <TableCell className="text-ink-muted">
                    {request.location || '—'}
                    {request.address && <div className="text-[11px]">{request.address}</div>}
                  </TableCell>
                  <TableCell className="text-ink-muted">{request.build || '—'}</TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={() => toggle(request)}
                      title="Toggle contacted"
                      className="cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_5px_var(--sss-ring)]"
                    >
                      <Badge variant={request.notified ? 'ink' : 'accent'}>
                        {request.notified ? 'Contacted' : 'Waiting'}
                      </Badge>
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminCard>
    </AdminLayout>
  )
}
